import { Injectable } from "@angular/core";
import { Platform, ToastController } from "@ionic/angular";

import { WindowRefService } from "./window-ref.service";

interface VersionStamp {
  buildId: string;
}

const VERSION_URL = "/version.json";
const POLL_INTERVAL_MS = 5 * 60 * 1000;

@Injectable({
  providedIn: "root",
})
export class VersionCheckService {
  private loadedBuildId: string | null = null;
  private promptedBuildId: string | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  constructor(
    private platform: Platform,
    private toastController: ToastController,
    private windowRefService: WindowRefService
  ) {}

  // Web only; the native apps update through the stores.
  start() {
    const isWeb = this.platform.is("mobileweb") || this.platform.is("desktop");

    if (!isWeb || this.intervalId !== null) {
      return;
    }

    this.check();

    this.intervalId = setInterval(() => this.check(), POLL_INTERVAL_MS);

    this.windowRefService.window.addEventListener("focus", () => this.check());
  }

  private async check() {
    const buildId = await this.fetchBuildId();

    if (!buildId) {
      return;
    }

    if (this.loadedBuildId === null) {
      this.loadedBuildId = buildId;
      return;
    }

    if (buildId !== this.loadedBuildId && buildId !== this.promptedBuildId) {
      this.promptedBuildId = buildId;
      this.promptForReload();
    }
  }

  // Not HttpClient: this must skip the API interceptors' auth headers.
  private async fetchBuildId(): Promise<string | null> {
    try {
      const response = await fetch(VERSION_URL, { cache: "no-store" });

      if (!response.ok) {
        return null;
      }

      const stamp = (await response.json()) as VersionStamp;

      return stamp?.buildId ?? null;
    } catch {
      return null;
    }
  }

  private async promptForReload() {
    const toast = await this.toastController.create({
      message: "A new version of the portal is available.",
      position: "top",
      color: "primary",
      buttons: [
        {
          text: "Reload",
          handler: () => this.windowRefService.window.location.reload(),
        },
        {
          text: "Later",
          role: "cancel",
        },
      ],
    });

    toast.present();
  }
}
